import { EdgeFunctionError, invokeEdgeFunction } from './invokeEdgeFunction';

export type NewsletterPreviewKind = 'raw' | 'template';

export const NEWSLETTER_PREVIEW_EMPTY_CODES = new Set([
  'rendered_body_empty',
  'content_empty',
]);

export function isNewsletterPreviewEmptyError(error: unknown): boolean {
  return getNewsletterPreviewEmptyCode(error) != null;
}

export function getNewsletterPreviewEmptyCode(error: unknown): string | undefined {
  if (error instanceof EdgeFunctionError && error.code) {
    return error.code;
  }
  if (error instanceof Error && NEWSLETTER_PREVIEW_EMPTY_CODES.has(error.message)) {
    return error.message;
  }
  return undefined;
}

export function newsletterPreviewEmptyMessage(code?: string): string {
  if (code === 'rendered_body_empty') {
    return 'Blocks are present but nothing renders yet. Add text to markdown blocks, HTML, or pick an element with body content.';
  }
  return 'Nothing to preview yet. Add content in Edit or JSON mode.';
}

export interface NewsletterPreviewResult {
  html: string;
  subject: string;
  previewKind: NewsletterPreviewKind;
  postmarkTemplateAlias?: string | null;
  warning?: string;
  error?: string;
  code?: string;
}

export interface PreviewNewsletterInput {
  newsletterId?: string | number | null;
  content?: unknown;
  name?: string | null;
  emailSubject?: string | null;
  postmarkTemplateAlias?: string | null;
  /** Inbox preview / preheader text (newsletter settings subtitle). */
  subtitle?: string | null;
}

/**
 * Server-side send preview (preview-newsletter edge function).
 */
export async function fetchNewsletterPreview(
  input: PreviewNewsletterInput,
): Promise<NewsletterPreviewResult> {
  const body: Record<string, unknown> = {};

  if (input.newsletterId != null && String(input.newsletterId).trim()) {
    body.newsletter_id = String(input.newsletterId);
  }
  if (input.content !== undefined) {
    body.content = input.content;
  }
  if (input.name !== undefined) {
    body.name = input.name;
  }
  if (input.emailSubject !== undefined) {
    body.email_subject = input.emailSubject;
  }
  if (input.postmarkTemplateAlias !== undefined) {
    body.postmark_template_alias = input.postmarkTemplateAlias;
  }
  if (input.subtitle !== undefined) {
    body.subtitle = input.subtitle;
  }

  const result = await invokeEdgeFunction<NewsletterPreviewResult>('preview-newsletter', body);
  if (result.error) {
    throw new Error(result.error);
  }
  if (!result.html) {
    throw new Error('Preview returned no HTML');
  }
  return result;
}
